// ---------------------------------------------------------------------------
// Every word this player has offered this round, with the one that is actually
// their answer marked.
//
// This is a RECORD, not a notification. The ruling that matters lands on the
// word box, at the point of entry, because a greyed row in a side panel is
// exactly what a player hunting for a longer word does not see. What is left
// here is the list of what has been tried, so it is marks rather than
// sentences: a red cross refused, a green tick going in, a grey tick counted
// but shorter than the answer. The words survive for a screen reader, which is
// also what the live region announces.
//
// Two shapes here are load-bearing rather than decorative.
//
// The box has a FIXED height and scrolls inside itself, in every state,
// including empty. A round is thirty seconds long and often played with one
// thumb, so a list that grew by a row each time it was used would walk the
// buttons out from under that thumb. Nothing above this can move because
// nothing about this changes size.
//
// Newest first, because the row a player just created is the one they are
// waiting on, and it should not require a scroll to see. Older tries fall away
// downward inside the box. A word offered a second time is not a second row —
// it is the same row, back at the top and waiting on a new ruling, because two
// rows for one string would be two rows claiming one answer.
// ---------------------------------------------------------------------------

import type { ReactElement } from 'react';
import { CheckIcon, CloseIcon, Icon } from '../primitives/index.js';
import type { WordOffer } from '../../lib/wordOffers.js';

/**
 * What the row's mark means, said for a reader who cannot see which mark it is.
 *
 * A word offered *after* the answer was set and not adopted is strictly shorter
 * than it, because the rule is longest wins and a tie goes to the later word —
 * anything at least as long would have taken the answer.
 *
 * The row that used to hold the answer is the exception, and it is the one the
 * old wording got wrong. Offering HOSE and then NOSE moves the answer to NOSE
 * and leaves HOSE sitting there at the same length, so calling it "shorter"
 * is a lie the player can check against their own list. It needs the answer's
 * length to tell the two apart; the length alone is enough, because a longer
 * word would be the answer.
 */
function verdictText(offer: WordOffer, answerLength: number | null): string {
  if (offer.status === 'pending') return 'checking';
  if (offer.status === 'invalid') return 'not a word';
  if (offer.isAnswer) return `your answer, ${offer.score}`;
  if (answerLength !== null && offer.word.length >= answerLength) {
    return 'beaten by a later word';
  }
  return 'shorter than your answer';
}

function Mark({ offer }: { offer: WordOffer }): ReactElement {
  if (offer.status === 'pending') {
    return (
      <span
        className="h-[7px] w-[7px] shrink-0 animate-pulse rounded-full bg-line-strong"
        aria-hidden="true"
      />
    );
  }
  return (
    <Icon
      icon={offer.status === 'valid' ? CheckIcon : CloseIcon}
      size={14}
      className={
        offer.status === 'invalid'
          ? 'shrink-0 text-alert'
          : offer.isAnswer
            ? 'shrink-0 text-good'
            : 'shrink-0 text-ink-dim'
      }
    />
  );
}

export function OfferedWords({ offers }: { offers: WordOffer[] }): ReactElement {
  // Newest first. A copy, because the store's array is not this component's to
  // reverse in place.
  const rows = [...offers].reverse();

  // Read once for the whole list rather than per row: every row is measured
  // against the same answer. Null while nothing has been adopted yet, which is
  // also the state where no row can claim to be beaten by one.
  const answerLength = offers.find((offer) => offer.isAnswer)?.word.length ?? null;

  return (
    <div className="flex flex-col gap-[6px]">
      <div className="flex items-baseline justify-between">
        <span className="label-mono">words tried</span>
        <span className="label-mono tabular">{offers.length}</span>
      </div>

      {/* A live region, so a ruling landing on a row is spoken as it lands: it
          is the only way a player not looking at this box learns that the word
          they just sent counted. Live on the list rather than a `role="log"`,
          which would take the list semantics off a list. */}
      <ul
        data-testid="offered-words"
        aria-live="polite"
        aria-label="Words you have tried"
        className="flex h-[104px] flex-col gap-[4px] overflow-y-auto rounded-tile border border-line bg-surface-2 p-[6px] min-[900px]:h-[196px]"
      >
        {rows.length === 0 ? (
          <li className="px-2 py-[6px] text-xs leading-normal text-ink-dim">Nothing yet.</li>
        ) : null}

        {rows.map((offer) => {
          return (
            <li
              key={offer.word}
              data-testid={`offer-${offer.word}`}
              data-offer-status={offer.status}
              data-answer={offer.isAnswer || undefined}
              // `fade-in` runs once, when the element is created. React keys
              // these by word, so a ruling reuses the row and does not replay
              // it, and the stylesheet's reduced-motion rule kills it outright.
              className={`fade-in flex shrink-0 flex-col gap-[2px] rounded-chip border px-2 py-[6px] transition-colors duration-300 ease-out ${
                offer.isAnswer
                  ? 'border-good bg-good/15'
                  : offer.status === 'invalid'
                    ? 'border-alert/50 bg-surface'
                    : 'border-transparent bg-surface'
              }`}
            >
              <div className="flex items-center gap-2">
                <Mark offer={offer} />
                <span
                  className={`font-mono text-[13px] font-semibold tracking-[0.06em] ${
                    offer.isAnswer
                      ? 'text-good'
                      : offer.status === 'invalid'
                        ? 'text-ink-dim'
                        : 'text-ink'
                  }`}
                >
                  {offer.word}
                </span>
                {/* The score is the one number worth the width, and only the
                    answer has one worth reading. Everything else the row could
                    say is in the mark. */}
                {offer.isAnswer ? (
                  <span className="tabular ml-auto font-mono text-[10px] font-medium text-good">
                    {offer.score}
                  </span>
                ) : null}
                <span className="sr-only">{verdictText(offer, answerLength)}</span>
              </div>

              {/* Said on the row rather than under the box, so the honest note
                  costs no height until there is something honest to say. */}
              {offer.restored ? (
                <span className="text-[11px] leading-normal text-ink-dim">
                  Back after a reload.
                </span>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
